import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { Order } from '../types';
import './Confirmation.css';

const Confirmation: React.FC = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<Order | null>(null);

  useEffect(() => {
    const savedOrder = localStorage.getItem('lastOrder');
    if (savedOrder) {
      setOrderData(JSON.parse(savedOrder));
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (!orderData) {
    return null;
  }

  const orderNumber = Math.floor(Math.random() * 90000) + 10000;
  const estimatedTime = orderData.deliveryType === 'delivery' ? '30-45 minutes' : '15-20 minutes';

  return (
    <div className="confirmation-page">
      <div className="container">
        <motion.div
          className="confirmation-content"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="success-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <FaCheckCircle />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Commande Confirmée !
          </motion.h1>

          <motion.p
            className="confirmation-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Merci pour votre commande. Nous avons bien reçu votre demande et nous la préparons avec soin.
          </motion.p>

          <motion.div
            className="order-number"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <span>Numéro de commande</span>
            <strong>#{orderNumber}</strong>
          </motion.div>

          <motion.div
            className="info-cards"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="info-card">
              <FaClock />
              <div>
                <h3>Temps estimé</h3>
                <p>{estimatedTime}</p>
              </div>
            </div>

            <div className="info-card">
              {orderData.deliveryType === 'delivery' ? <FaMapMarkerAlt /> : <FaPhone />}
              <div>
                <h3>{orderData.deliveryType === 'delivery' ? 'Livraison à' : 'Retrait à'}</h3>
                <p>
                  {orderData.deliveryType === 'delivery'
                    ? `${orderData.formData.address}, ${orderData.formData.city}`
                    : '81 Rue Bernard Palissy, 45500 Gien'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="order-details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h2>Détails de la commande</h2>
            <div className="order-items">
              {orderData.cart.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-info">
                    <div className="item-emoji">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div>
                      <strong>{item.name}</strong>
                      {item.options && item.options.length > 0 && (
                        <p className="item-options">
                          {item.options.map(opt => opt.name).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="item-quantity">x{item.quantity}</div>
                  <div className="item-price">
                    {((item.price + (item.options?.reduce((sum, opt) => sum + opt.price, 0) || 0)) * item.quantity).toFixed(2)}€
                  </div>
                </div>
              ))}
            </div>

            <div className="order-total">
              <span>Total</span>
              <strong>{orderData.total.toFixed(2)}€</strong>
            </div>
          </motion.div>

          <motion.div
            className="contact-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h3>Besoin d'aide ?</h3>
            <p>Contactez-nous au <strong>02 18 49 30 80</strong></p>
          </motion.div>

          <motion.div
            className="action-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <motion.button
              className="btn-primary"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Retour à l'Accueil
            </motion.button>
            <motion.button
              className="btn-secondary"
              onClick={() => navigate('/menu')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Nouvelle Commande
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Confirmation;
